import { useCallback, useEffect, useState } from "react";

import { fetchMe, getToken, setToken } from "./api/client";
import type { Me } from "./api/types";
import { Chat } from "./components/Chat";
import { ConflictBanner } from "./components/ConflictBanner";
import { FilterBar } from "./components/FilterBar";
import { Login } from "./components/Login";
import { Panel } from "./components/Panel";
import { Reference } from "./components/Reference";
import { TOOLS } from "./components/tools";
import { ConflictsProvider } from "./state/conflicts";
import { StoreProvider } from "./state/store";

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);

  const load = useCallback(() => {
    if (!getToken()) {
      setMe(null);
      setChecked(true);
      return;
    }
    fetchMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecked(true));
  }, []);

  useEffect(load, [load]);

  if (!checked) return <div className="boot">Загрузка…</div>;
  if (!me) return <Login onDone={load} />;

  return (
    <StoreProvider me={me}>
      <ConflictsProvider>
        <Dashboard
          me={me}
          onLogout={() => {
            setToken(null);
            setMe(null);
          }}
        />
      </ConflictsProvider>
    </StoreProvider>
  );
}

/** Справочники в адресной строке, а не в локальном состоянии.
 *
 * Ссылку на вид можно переслать коллеге — это уже обещано для фильтров, и
 * второй вид не должен быть исключением. `?view=reference` не конфликтует с
 * состоянием фильтров: `stateFromQuery` читает только свои ключи.
 */
function readView(): "dashboard" | "reference" {
  return new URLSearchParams(window.location.search).get("view") === "reference"
    ? "reference"
    : "dashboard";
}

function writeView(view: "dashboard" | "reference") {
  const params = new URLSearchParams(window.location.search);
  if (view === "reference") params.set("view", "reference");
  else params.delete("view");
  const query = params.toString();
  window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
}

function Dashboard({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [view, setView] = useState<"dashboard" | "reference">(readView);

  const go = (next: "dashboard" | "reference") => {
    writeView(next);
    setView(next);
  };

  return (
    <div className="app">
      <header className="topbar">
        <h1 className="topbar__title">Ходорковский LIVE — аналитика</h1>
        <div className="topbar__right">
          {!me.can_see_revenue && <span className="chip chip--muted">доход скрыт</span>}
          <span className="muted">{me.email}</span>
          {/* Кнопка только администратору. Это удобство, а не защита: запись в
              справочники режет сервер по IsAdminUser. */}
          {me.is_admin && view === "dashboard" && (
            <button type="button" className="button button--ghost" onClick={() => go("reference")}>
              Справочники
            </button>
          )}
          <button type="button" className="button button--ghost" onClick={onLogout}>
            Выйти
          </button>
        </div>
      </header>

      {view === "reference" && me.is_admin ? (
        <Reference onClose={() => go("dashboard")} />
      ) : (
        <>
          <FilterBar />
          <ConflictBanner />

          <div className="layout">
            <div className="grid">
              {TOOLS.map((spec) => (
                <Panel key={spec.key} spec={spec} />
              ))}
            </div>
            <Chat />
          </div>
        </>
      )}

      <footer className="footer muted">
        Shorts вне расчёта: они дают больше половины просмотров канала и вынесены на отдельный этап.
      </footer>
    </div>
  );
}
